// @ts-check

/** @file AwesomeTasks ownership adapter for Velocious's reusable SyncCoordinator. */

import SyncCoordinator from "velocious/build/src/sync/sync-coordinator.js"

import {PROJECT_REPLICA_DATABASE_IDENTIFIER} from "../database/project-replica-contract.js"
import {registerProjectReplicaTransportHandle, unregisterProjectReplicaTransportHandle} from "../database/project-replica-runtime.js"
import {createProjectReplicaStatusStore} from "../database/project-replica-status-store.js"
import {projectSyncConnectivity} from "./project-sync-connectivity.js"
import {classifyProjectSyncError} from "./project-sync-error.js"

/** @import TenantHandle from "velocious/build/src/tenants/tenant-handle.js" */
/** @import {SyncCoordinatorConnectivity, SyncCoordinatorScheduler, SyncCoordinatorStatusStore} from "velocious/build/src/sync/sync-coordinator-types.js" */
/** @import {ProjectReplicaIdentity} from "../database/project-replica-contract.js" */
/** @import ProjectSyncClient from "./project-sync-client.js" */

/**
 * Constructs the application policy around one tenant-bound SyncClient.
 * @param {object} args Adapter inputs.
 * @param {TenantHandle} args.handle Captured replica.
 * @param {ProjectReplicaIdentity} args.identity Canonical user/project owner.
 * @param {ProjectSyncClient} args.syncClient Tenant-bound application client.
 * @param {SyncCoordinatorConnectivity} [args.connectivity] Online-state notification source.
 * @param {(error: Error) => {code: string, retryable: boolean}} [args.classifyError] Safe classifier.
 * @param {() => Date} [args.now] Clock.
 * @param {{initialDelayMs?: number, maxAttempts?: number, maxDelayMs?: number}} [args.retry] Bounded retry limits.
 * @param {SyncCoordinatorScheduler} [args.scheduler] Injectable timer owner.
 * @param {SyncCoordinatorStatusStore} [args.statusStore] Durable privacy-safe metadata owner.
 * @param {() => Promise<void>} [args.beforeStatusSave] Durable mutation-write drain.
 * @returns {SyncCoordinator} Configured generation-safe lifecycle owner.
 */
export function createProjectSyncCoordinator({
  beforeStatusSave = async () => {},
  classifyError = classifyProjectSyncError,
  connectivity = projectSyncConnectivity,
  handle,
  identity,
  now,
  retry,
  scheduler,
  statusStore = createProjectReplicaStatusStore(handle, {beforeSave: beforeStatusSave}),
  syncClient
}) {
  return new SyncCoordinator({
    classifyError,
    connectivity,
    ...(now ? {now} : {}),
    prepare: async ({signal}) => {
      const releasePin = await acquireCoordinatorPin({handle, signal})

      registerProjectReplicaTransportHandle(identity, handle)
      try {
        await syncClient.activateUserScope()
        await syncClient.applyStoredConflictAuthority(signal)
      } catch (error) {
        unregisterProjectReplicaTransportHandle(identity, handle)
        await releasePin()
        throw error
      }

      return async () => {
        await beforeStatusSave()
        unregisterProjectReplicaTransportHandle(identity, handle)
        await releasePin()
      }
    },
    realtime: Boolean(syncClient.config.websocketUrl || syncClient.config.websocketClient || syncClient.config.realtime),
    ...(retry ? {retry} : {}),
    ...(scheduler ? {scheduler} : {}),
    statusStore,
    syncClient
  })
}

/**
 * Holds the framework pin for the coordinator generation without adding a
 * second handle pool.
 * @param {{handle: TenantHandle, signal: AbortSignal}} args Pin inputs.
 * @returns {Promise<() => Promise<void>>} Idempotent release.
 */
async function acquireCoordinatorPin({handle, signal}) {
  const entered = deferred()
  const releaseGate = deferred()
  let released = false
  const pinPromise = handle.withPin({databaseIdentifier: PROJECT_REPLICA_DATABASE_IDENTIFIER}, async () => {
    if (signal.aborted) throw signal.reason
    entered.resolve()
    await releaseGate.promise
  })

  pinPromise.catch(entered.reject)
  await entered.promise

  return async () => {
    if (released) return
    released = true
    releaseGate.resolve()
    await pinPromise
  }
}

/**
 * Creates a promise gate used to hold a generation-scoped replica pin.
 * @returns {{promise: Promise<void>, reject: (error: unknown) => void, resolve: () => void}} Promise gate.
 */
function deferred() {
  let reject = (_error) => {}
  let resolve = () => {}
  const promise = new Promise((promiseResolve, promiseReject) => {
    reject = promiseReject
    resolve = () => promiseResolve(undefined)
  })

  return {promise, reject, resolve}
}
